<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import TieredPricingCards from '@/Components/Forms/TieredPricingCards.vue'
import RevenueCalculator from './RevenueCalculator.vue'
import AvailabilityScheduler from '@/Components/Forms/AvailabilityScheduler.vue'
import NavigationButtons from '@/Components/Onboarding/NavigationButtons.vue'
import { Check } from 'lucide-vue-next'

const props = defineProps({
  pricingLimits: {
    type: Object,
    default: () => ({ gold: { min: 0.99, average: 50, max: 100 } }),
  },
  maxOneToOneSessions: {
    type: Number,
    default: 10
  },
  teachingMethodIds: {
    type: Array,
    default: () => []
  },
  offerings: {
    type: Array,
    default: () => []
  },
  application: {
    type: Object,
    default: null
  },
  slug: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['next', 'back'])

const loading = ref(false)
const errors = ref({})

// Pricing state - initialize with the minimum price from server
const pricing = ref({ gold: props.pricingLimits.gold?.min || 0.99 })

// Availability state
const selectedOneOnOneSlots = ref([])

// Function to populate data from application
const populateFromApplication = () => {
  if (!props.application) return

  if (props.application.pricing?.gold) {
    pricing.value.gold = props.application.pricing.gold
  }

  if (props.application.one_to_one_availability) {
    selectedOneOnOneSlots.value = props.application.one_to_one_availability
  }
}

onMounted(() => {
  populateFromApplication()
})

watch(() => props.application, () => {
  populateFromApplication()
}, { deep: true })

/**
 * Whether this applicant is being asked for a price at all.
 *
 * This used to match teaching method names — `includes('group')` meant Bindle
 * Live — so renaming a row in the database changed which prices a tutor was
 * asked for. Each offering declares its tier, and an offering is recognised by
 * the capability that identifies it, because study material belongs to the
 * product rather than identifying it.
 */
const hasGoldMethods = computed(() => props.offerings
  .filter(offering => props.teachingMethodIds.includes(offering.primary_method_id))
  .some(offering => offering.tier === 'gold')
)

const oneOnOneSlotsCount = computed(() => selectedOneOnOneSlots.value.length)

const handlePricingUpdate = (newPricing) => {
  pricing.value = { gold: hasGoldMethods.value ? newPricing.gold : 0 }

  if (errors.value['pricing.gold']) {
    errors.value['pricing.gold'] = ''
  }
}

// Clear availability errors when user selects slots
watch(selectedOneOnOneSlots, () => {
  if (errors.value.oneOnOneAvailability) {
    errors.value.oneOnOneAvailability = ''
  }
})

const submitForm = async () => {
  errors.value = {}

  // Validate 1:1 session availability
  if (hasGoldMethods.value && selectedOneOnOneSlots.value.length === 0) {
    errors.value.oneOnOneAvailability = 'Please select at least one 1:1 tutoring session slot'

    // Scroll to error message
    setTimeout(() => {
      const errorElement = document.getElementById('one-on-one-availability-error')
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  // Validate 1:1 sessions don't exceed max
  if (hasGoldMethods.value && props.maxOneToOneSessions > 0 && selectedOneOnOneSlots.value.length > props.maxOneToOneSessions) {
    errors.value.oneOnOneAvailability = `You can select a maximum of ${props.maxOneToOneSessions} 1:1 session slot(s). Please remove some slots.`

    setTimeout(() => {
      const errorElement = document.getElementById('one-on-one-availability-error')
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  loading.value = true

  // Prepare form data - use JSON structure for pricing
  const formData = {
    pricing: {
      gold: hasGoldMethods.value ? pricing.value.gold : null,
    },
    availability: {
      one_on_one_sessions: hasGoldMethods.value ? selectedOneOnOneSlots.value : [],
    },
  }

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/pricing-availability', {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    loading.value = false
    emit('next', data)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}

const saveDraft = async () => {
  loading.value = true
  errors.value = {}

  // Prepare form data
  const formData = {
    pricing: {
      gold: hasGoldMethods.value ? pricing.value.gold : null,
    },
    availability: {
      one_on_one_sessions: hasGoldMethods.value ? selectedOneOnOneSlots.value : [],
    },
    is_draft: true // Indicate this is a draft save
  }

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/pricing-availability', {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save draft. Please try again.' }
      }
      loading.value = false
      return
    }

    loading.value = false

    // Show success message
    // Says where the draft actually lives. It is keyed to a slug in
    // localStorage, so it does not follow the applicant to another
    // browser or device — "saved successfully" implied otherwise.
    errors.value = { success: 'Draft saved. It is kept in this browser on this device, so come back on the same one to carry on.' }
    setTimeout(() => {
      errors.value = {}
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred while saving draft. Please try again.' }
    loading.value = false
  }
}

const goBack = () => {
  emit('back')
}
</script>

<template>
  <main class="max-w-7xl mx-auto px-0 md:px-8 py-8">
    <!-- Success Message -->
    <div v-if="errors.success" class="mb-6 bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
      <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
        <span class="material-symbols-outlined text-white text-xl">check_circle</span>
      </div>
      <div>
        <p class="text-green-900 font-semibold">Success</p>
        <p class="text-green-700 text-sm">{{ errors.success }}</p>
      </div>
    </div>

    <!-- General Error Message -->
    <div v-if="errors.general" class="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
      <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
        <span class="material-symbols-outlined text-white text-xl">error</span>
      </div>
      <div>
        <p class="text-red-900 font-semibold">Error</p>
        <p class="text-red-700 text-sm">{{ errors.general }}</p>
      </div>
    </div>

    <!-- Intro Section -->
    <section id="intro-section" class="mb-8">
      <div class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-4 md:p-8 border border-green-100">
        <div class="flex items-start gap-4">
          <div class="w-14 h-14 bg-brand-green rounded-xl flex items-center justify-center shrink-0">
            <span class="material-symbols-outlined text-white text-xl">currency_pound</span>
          </div>
          <div class="flex-1">
            <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">Your Weekly Price</h2>
            <p class="hidden md:block text-gray-600 text-base leading-relaxed">
              Students subscribe weekly for a one-to-one lesson and your study material. Move the slider to set what
              that costs. You can change it later from your settings.
            </p>
            <p class="md:hidden text-gray-600 text-base leading-relaxed">
              Students subscribe weekly for a lesson and your material. Set what that costs.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tiered Pricing Cards -->
    <TieredPricingCards
      :pricing-limits="pricingLimits"
      :initial-pricing="pricing"
      :has-gold-methods="hasGoldMethods"
      :errors="errors"
      @update:pricing="handlePricingUpdate"
    />

    <!-- Revenue Calculator -->
    <RevenueCalculator
      :gold-plan-price="pricing.gold"
      :gold-plan-offered="hasGoldMethods"
    />

    <!-- Weekly Availability -->
    <section id="weekly-availability" class="mb-8" v-if="hasGoldMethods">
      <div class="bg-white rounded-2xl p-4 md:p-8 border border-gray-200">
        <div class="flex flex-col md:flex-col md:items-center justify-between mb-6 gap-4">
          <div>
            <h3 class="text-xl font-bold text-gray-900">Weekly Availability</h3>
            <p class="text-sm text-gray-500 mt-1">Set the hours you are free to teach</p>
          </div>
          <div class="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
            <span class="material-symbols-outlined text-brand-blue">info</span>
            <span class="text-sm text-gray-700">
              <span class="font-semibold text-brand-blue">{{ oneOnOneSlotsCount }}</span> slots selected
            </span>
          </div>
        </div>

        <!-- 1:1 Tutoring Sessions -->
        <div v-if="hasGoldMethods" class="mb-8">
          <!-- Error Message -->
          <div v-if="errors.oneOnOneAvailability" id="one-on-one-availability-error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined shrink-0 text-[1.25rem] leading-5 text-danger">error</span>
              <p class="text-red-600 font-medium text-sm">{{ errors.oneOnOneAvailability }}</p>
            </div>
          </div>

          <AvailabilityScheduler
            title="1:1 Tutoring Sessions"
            :description="maxOneToOneSessions > 0 ? `Select up to ${maxOneToOneSessions} time slot(s) for individual tutoring` : 'Select available time slots for individual tutoring'"
            icon="person"
            icon-bg-color="bg-yellow-100"
            icon-text-color="text-yellow-600"
            :selected-slots="selectedOneOnOneSlots"
            @update:selected-slots="selectedOneOnOneSlots = $event"
            :multi-select="true"
            selected-color="brand"
            :show-slot-details="true"
            :max-total-slots="maxOneToOneSessions"
          />
        </div>
      </div>
    </section>

    <!-- Tips Section -->
    <section id="tips-section" class="mb-8">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!--
          Icon and heading share a row; the list sits below both.
          The tile is 48px tall and the heading line is 28, so aligning them at
          the top left the tile overhanging into the first bullet — the circle
          straddled the heading and the line under it and read as misplaced.
        -->
        <div class="rounded-2xl border border-line bg-brand-soft p-8">
          <div class="mb-4 flex items-center gap-4">
            <span class="grid size-12 shrink-0 place-items-center rounded-xl bg-brand">
              <span class="material-symbols-outlined text-xl text-brand-contrast">lightbulb</span>
            </span>
            <h4 class="text-lg font-bold text-ink">Pricing Tips</h4>
          </div>
          <ul class="space-y-2 text-sm text-muted">
            <li class="flex items-start gap-2">
              <Check class="mt-0.5 size-4 shrink-0 text-brand" />
              <span>Research competitor pricing in your subject area</span>
            </li>
            <li class="flex items-start gap-2">
              <Check class="mt-0.5 size-4 shrink-0 text-brand" />
              <span>Consider your experience level and qualifications</span>
            </li>
            <li class="flex items-start gap-2">
              <Check class="mt-0.5 size-4 shrink-0 text-brand" />
              <span>Start competitive and adjust based on demand</span>
            </li>
          </ul>
        </div>
        <div class="rounded-2xl border border-line bg-brand-soft p-8">
          <div class="mb-4 flex items-center gap-4">
            <span class="grid size-12 shrink-0 place-items-center rounded-xl bg-brand">
              <span class="material-symbols-outlined text-xl text-brand-contrast">star</span>
            </span>
            <h4 class="text-lg font-bold text-ink">Value Proposition</h4>
          </div>
          <ul class="space-y-2 text-sm text-muted">
            <li class="flex items-start gap-2">
              <Check class="mt-0.5 size-4 shrink-0 text-brand" />
              <span>Highlight your unique teaching methods</span>
            </li>
            <li class="flex items-start gap-2">
              <Check class="mt-0.5 size-4 shrink-0 text-brand" />
              <span>Emphasize the personal attention a 1:1 lesson gives</span>
            </li>
            <li class="flex items-start gap-2">
              <Check class="mt-0.5 size-4 shrink-0 text-brand" />
              <span>Say what a student gets from a week with you</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

  </main>

  <!-- Navigation Buttons -->
  <NavigationButtons
    :loading="loading"
    :show-previous="true"
    primary-button-text="Continue to Bank Details"
    primary-button-loading-text="Saving..."
    @previous="goBack"
    @save-draft="saveDraft"
    @submit="submitForm"
  />
</template>
